import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Container,
    Grid,
    Card,
    CardContent,
    Typography,
    Box,
    Button,
    Alert,
    CircularProgress,
    Chip,
    Paper,
    IconButton,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Tooltip
} from '@mui/material';
import {
    TrendingUp as TrendingUpIcon,
    AttachMoney as MoneyIcon,
    Assessment as AssessmentIcon,
    Refresh as RefreshIcon,
    Analytics as AnalyticsIcon,
    FileDownload as ExportIcon,
    PictureAsPdf as PdfIcon,
    TableChart as ExcelIcon,
    DataSaverOff as CsvIcon,
    Dashboard as DashboardIcon,
    TableChart as TableChartIcon,
    Visibility as VisibilityIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import {
    LineChart,
    Line,
    PieChart,
    Pie,
    Cell,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip as RechartsTooltip,
    Legend,
    ResponsiveContainer,
    BarChart,
    Bar
} from 'recharts';
import { useEnhancedFinancialDashboard } from '../../hooks/useEnhancedFinancialDashboard';
import { canAccessFinancialDashboard } from '../../constants/financialAccess';
import { useAuth } from '../../contexts/AuthContext';
import { useDashboardScope } from '../../contexts/DashboardScopeContext';
import { DEFAULT_DASHBOARD_PROJECT_ID } from '../../utils/dashboardScope';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { buildProjectOpsWedgeUrl } from '../../constants/projectOpsTabs';
import apiClient from '../../services/apiClient';
import DashboardScopeBar from './shared/DashboardScopeBar';
import DashboardLoadingState from './shared/DashboardLoadingState';
import DashboardActionInbox from './shared/DashboardActionInbox';
import {
    formatDashboardDateRangeLabel,
    getDashboardPeriodLabel,
} from '../../utils/dashboardScope';
import { buildScopedFinancialMetrics } from '../../utils/financialScopedMetrics';

// Import new PSA-standard financial components
import {
    ExecutiveSummaryBar,
    FinancialFilters,
    DrillDownDialog,
    FinancialWorkItemsPanel,
    FinancialEngagementKpiGrid,
    FINANCIAL_CHART_COLORS
} from './financial';

// Chart colors - use PSA-standard palette
const CHART_COLORS = FINANCIAL_CHART_COLORS
    ? [FINANCIAL_CHART_COLORS.revenue, FINANCIAL_CHART_COLORS.profit, FINANCIAL_CHART_COLORS.wip,
    FINANCIAL_CHART_COLORS.negative, FINANCIAL_CHART_COLORS.forecast, FINANCIAL_CHART_COLORS.ar]
    : ['#2196f3', '#4caf50', '#ff9800', '#f44336', '#9c27b0', '#00bcd4'];

/**
 * Enhanced Financial Management Dashboard
 * Combines original FinancialDashboard with merged CostDashboard analytics
 * Provides comprehensive financial management interface for PSA platform
 */
const EnhancedFinancialDashboard = () => {
    const { user } = useAuth();
    const [searchParams] = useSearchParams();
    const {
        timeRange: financialTimeframe,
        dateRange: financialDateRange,
        projectId: scopedProjectId,
        setProjectId
    } = useDashboardScope();
    const navigate = useNavigate();

    useEffect(() => {
        const urlProject = searchParams.get('project');
        if (urlProject && urlProject !== scopedProjectId) {
            setProjectId(urlProject);
        }
    }, [searchParams, scopedProjectId, setProjectId]);

    const isProjectScoped = Boolean(
        scopedProjectId && scopedProjectId !== DEFAULT_DASHBOARD_PROJECT_ID
    );
    const {
        // Data
        profitabilityData,
        revenueForecasting,
        costAnalytics,
        billRateData,
        qualitySummary,
        realizationData,
        wipData,
        backlogData,
        contributionMarginData,
        utilizationData,
        arAgingData,
        cashFlowData,
        expenseRatiosData,
        benchmarkCompareData,
        revenueTriadData,
        periodComparisonData,
        showComparison,
        loading,
        endpointLoadingState,
        error,
        filters,

        // Operations
        refreshFinancialData,
        updateFilters,
        exportData,
        clearError,
        toggleComparison,

        // Utilities
        formatCurrency,
        formatPercentage
    } = useEnhancedFinancialDashboard();

    const refreshFinancialDataRef = useRef(refreshFinancialData);
    refreshFinancialDataRef.current = refreshFinancialData;

    // Utilization data is now handled by useEnhancedFinancialDashboard

    // Local state for UI
    const [activeTab, setActiveTab] = useState(0);
    const [projectDetailOpen, setProjectDetailOpen] = useState(false);
    const [selectedProjectData, setSelectedProjectData] = useState(null);
    const [projectDetailsLoading, setProjectDetailsLoading] = useState(false);

    // Drill-down dialog state
    const [drillDownOpen, setDrillDownOpen] = useState(false);
    const [financialWorkQueueStats, setFinancialWorkQueueStats] = useState({ total: 0, high: 0 });
    const [drillDownConfig, setDrillDownConfig] = useState({
        title: '',
        subtitle: '',
        data: [],
        columns: []
    });

    const clients = useMemo(() => {
        const projectsData = costAnalytics?.budgetVariance?.projects || [];
        const projectClientMap = new Map();

        projectsData.forEach((project) => {
            const clientId = project?.client_id || project?.client?.id || project?.clientId;
            const clientName = project?.client_name || project?.client?.name || project?.client;

            if (!clientName && clientId == null) return;

            const mapKey = clientId != null ? `id:${clientId}` : `name:${clientName}`;
            if (!projectClientMap.has(mapKey)) {
                projectClientMap.set(mapKey, {
                    id: clientId != null ? clientId : mapKey,
                    name: String(clientName || 'Unknown Client')
                });
            }
        });

        return Array.from(projectClientMap.values());
    }, [costAnalytics?.budgetVariance?.projects]);

    const asDisplayValue = (value) => {
        if (value === null || value === undefined || Number.isNaN(Number(value))) {
            return null;
        }
        return Number(value);
    };
    const formatNullablePercent = (value) => {
        if (value === null || value === undefined || Number.isNaN(Number(value))) {
            return 'N/A';
        }
        return `${Number(value).toFixed(1)}%`;
    };
    const formatNullableRate = (value) => {
        if (value === null || value === undefined || Number.isNaN(Number(value))) {
            return 'N/A';
        }
        return Number(value).toFixed(2);
    };

    const projects = useMemo(() => {
        const projectsData = costAnalytics?.budgetVariance?.projects || [];
        const projectMap = new Map();

        projectsData.forEach((project) => {
            const projectId = project?.id || project?.project_id;
            if (projectId == null) return;

            if (!projectMap.has(String(projectId))) {
                projectMap.set(String(projectId), {
                    id: projectId,
                    name: String(project?.name || `Project ${projectId}`)
                });
            }
        });

        return Array.from(projectMap.values());
    }, [costAnalytics?.budgetVariance?.projects]);

    // PSA Integration: Listen for expense approval events
    useEffect(() => {
        const handleExpenseEvent = (event) => {
            // Refresh financial data instead of reloading the page
            refreshFinancialDataRef.current().catch((error) => {
                console.error('Financial dashboard refresh failed (expense event handler):', error);
            });

            setTimeout(() => {
                refreshFinancialDataRef.current().catch((error) => {
                    console.error('Financial dashboard follow-up refresh failed (expense event handler):', error);
                });
            }, 2000);
        };

        window.addEventListener('expenseApproved', handleExpenseEvent);

        return () => {
            window.removeEventListener('expenseApproved', handleExpenseEvent);
        };
    }, []);

    // Event handlers
    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    const handleExport = async (format) => {
        const result = await exportData(format);
        if (result?.success) {
            // Success - file downloaded
            console.log(`Successfully exported: ${result.filename}`);
        } else if (result?.error) {
            // Show error to user
            alert(result.error);
        }
    };

    // Project detail handlers - fetches real financial data from backend
    const handleViewProject = async (project) => {
        setProjectDetailsLoading(true);
        // Set initial data from table (already computed)
        setSelectedProjectData(project);
        setProjectDetailOpen(true);

        try {
            // Fetch additional project financial details from backend
            const response = await apiClient.get(`financial-engine/financial-metrics/${project.id}/`, {
                timeout: 10000
            });

            if (response.data?.success !== false) {
                const financialData = response.data?.data || response.data;
                // Merge backend data with existing project data
                setSelectedProjectData(prev => ({
                    ...prev,
                    // Add additional metrics from backend if available
                    eac: financialData.eac,
                    etc: financialData.etc,
                    burn_rate: financialData.burn_rate,
                    cost_trend: financialData.cost_trend,
                    completion_percentage: financialData.completion_percentage,
                }));
            }
        } catch (err) {
            // Log error but don't fail - dialog still shows with initial data
            console.error('Failed to fetch detailed project metrics:', err);
        } finally {
            setProjectDetailsLoading(false);
        }
    };

    const handleCloseProjectDetail = () => {
        setProjectDetailOpen(false);
        setSelectedProjectData(null);
        setProjectDetailsLoading(false);
    };

    const handleReallocateResources = () => {
        const scopedProjectId = selectedProjectData?.id ?? selectedProjectData?.project_id;
        if (!scopedProjectId) return;

        // Scope the next workflow screen to the project that triggered the variance alert.
        setProjectId(scopedProjectId);
        handleCloseProjectDetail();
        navigate('/resource-planning');
    };

    // Utilization data is now loaded automatically by useEnhancedFinancialDashboard

    // Access control early return (after hooks)
    if (!canAccessFinancialDashboard(user)) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Alert severity="error">
                    <Typography variant="h6">Access Denied</Typography>
                    <Typography>
                        Financial dashboard access is restricted to finance, accounting,
                        director, manager, and administrator roles.
                    </Typography>
                </Alert>
            </Container>
        );
    }

    // Tab panel component
    const TabPanel = ({ children, value, index, ...other }) => (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`financial-tabpanel-${index}`}
            aria-labelledby={`financial-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
        </div>
    );

    // Helper functions for project analysis
    const getStatusColor = (status) => {
        switch (status) {
            case 'active': return 'success';
            case 'completed': return 'primary';
            case 'on_hold': return 'warning';
            default: return 'default';
        }
    };

    const getContractTypeColor = (contractType) => {
        switch (contractType) {
            case 'lump_sum': return 'primary';
            case 'hourly': return 'secondary';
            default: return 'default';
        }
    };

    const getProfitColor = (profit) => {
        if (profit > 0) return 'success.main';
        if (profit < 0) return 'error.main';
        return 'text.primary';
    };

    const getHealthColor = (score) => {
        if (score >= 80) return 'success';
        if (score >= 60) return 'warning';
        return 'error';
    };

    // Project Financial Details Table Component (Legacy Cost Dashboard Integration)
    const ProjectFinancialDetailsTable = () => {
        const projects = costAnalytics?.budgetVariance?.projects || [];

        const calculateHealthScore = (project) => {
            let score = 50; // Base score

            // Budget performance (30 points)
            if (project.variance_percentage < 0) score += 30; // Under budget
            else if (project.variance_percentage < 10) score += 20; // Close to budget
            else if (project.variance_percentage < 20) score += 10; // Slightly over
            // else 0 points for significantly over budget

            // Profitability (40 points)
            const profitMargin = asDisplayValue(project.profit_margin);
            if (profitMargin === null) {
                score -= 5;
            } else if (profitMargin > 20) score += 40;
            else if (profitMargin > 10) score += 30;
            else if (profitMargin > 0) score += 20;
            else if (profitMargin > -10) score += 10;
            // else 0 points for significant losses

            // Status (30 points)
            if (project.status === 'completed') score += 30;
            else if (project.status === 'active') score += 20;
            else if (project.status === 'on_hold') score += 10;

            return Math.min(100, Math.max(0, score));
        };



        return (
            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                        <Typography variant="h6">
                            Project Financial Performance
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            Industry-standard PSA project drill-down analysis
                        </Typography>
                    </Box>

                    <TableContainer component={Paper}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Project Name</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Contract Type</TableCell>
                                    <TableCell align="right">Budget</TableCell>
                                    <TableCell align="right">Actual Cost</TableCell>
                                    <TableCell align="right">Revenue</TableCell>
                                    <TableCell align="right">Profit/Loss</TableCell>
                                    <TableCell align="right">Margin %</TableCell>
                                    <TableCell align="right">Health Score</TableCell>
                                    <TableCell align="center">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {projects.map((project) => {
                                    const healthScore = calculateHealthScore(project);

                                    // Values are expected to be precomputed by backend canonical KPI sources.
                                    const revenue = asDisplayValue(project.total_revenue);
                                    const costs = asDisplayValue(project.total_costs) ?? asDisplayValue(project.actual_cost);
                                    const profitLoss = asDisplayValue(project.profit_loss);
                                    const profitMargin = asDisplayValue(project.profit_margin);
                                    const resolvedProfitLoss = profitLoss !== null && profitLoss !== undefined
                                        ? profitLoss
                                        : (revenue !== null && costs !== null ? revenue - costs : null);

                                    return (
                                        <TableRow key={project.id} hover>
                                            <TableCell>
                                                <Box>
                                                    <Typography variant="body2" fontWeight="medium">
                                                        {project.name}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {project.client_name || 'No client assigned'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                {project.status ? (
                                                    <Chip
                                                        label={project.status}
                                                        color={getStatusColor(project.status)}
                                                        size="small"
                                                    />
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">Not set</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {project.contract_type ? (
                                                    <Chip
                                                        label={project.contract_type === 'lump_sum' ? 'Lump Sum' : 'Hourly'}
                                                        color={getContractTypeColor(project.contract_type)}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">Not set</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {project.budget !== null ? formatCurrency(project.budget) : 'N/A'}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Box>
                                                    <Typography variant="body2">
                                                        {costs !== null ? formatCurrency(costs) : 'N/A'}
                                                    </Typography>
                                                    <Typography variant="caption" color="text.secondary">
                                                        Variance: {project.variance !== null ? formatCurrency(project.variance) : 'N/A'}
                                                    </Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell align="right">
                                                {revenue !== null ? formatCurrency(revenue) : 'N/A'}
                                            </TableCell>
                                            <TableCell align="right">
                                                {resolvedProfitLoss !== null ? (
                                                    <Typography color={getProfitColor(resolvedProfitLoss)}>
                                                        {formatCurrency(resolvedProfitLoss)}
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">N/A</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                {profitMargin !== null ? (
                                                    <Typography color={getProfitColor(profitMargin)}>
                                                        {formatNullablePercent(profitMargin)}
                                                    </Typography>
                                                ) : (
                                                    <Typography variant="caption" color="text.secondary">N/A</Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                                <Chip
                                                    label={`${healthScore}%`}
                                                    color={getHealthColor(healthScore)}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="center">
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleViewProject({
                                                        ...project,
                                                        profit_loss: resolvedProfitLoss,
                                                        profit_margin: profitMargin,
                                                        health_score: healthScore,
                                                        billable_amount: revenue,
                                                        actual_cost: costs
                                                    })}
                                                    disabled={loading}
                                                >
                                                    <VisibilityIcon />
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    {projects.length === 0 && (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                {error || costAnalytics?.exportCapabilities?.status === 'error'
                                    ? 'Project Financial Data Unavailable'
                                    : 'No project financial data available'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {error
                                    ? `Error loading data: ${error}`
                                    : costAnalytics?.exportCapabilities?.status === 'error'
                                        ? 'Unable to connect to financial data source. Please check your connection and try again.'
                                        : 'No projects found to display financial data.'}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                {error || costAnalytics?.exportCapabilities?.status === 'error'
                                    ? 'Try refreshing the page or contact support if the issue persists.'
                                    : 'Create some projects or adjust your filters to see financial data.'}
                            </Typography>
                        </Box>
                    )}
                </CardContent>
            </Card>
        );
    };

    // Project Detail Dialog Component (Industry Standard PSA Feature)
    const ProjectDetailDialog = () => {
        if (!selectedProjectData) return null;

        return (
            <Dialog
                open={projectDetailOpen}
                onClose={handleCloseProjectDetail}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { height: '90vh' }
                }}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="h6">{selectedProjectData.name}</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Financial Performance Analysis
                            </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                            <Chip
                                label={selectedProjectData.status}
                                color={getStatusColor(selectedProjectData.status)}
                                size="small"
                            />
                            <IconButton onClick={handleCloseProjectDetail}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {projectDetailsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
                            <CircularProgress />
                        </Box>
                    ) : (
                        <Grid container spacing={3}>
                            {/* Financial Summary */}
                            <Grid size={12}>
                                <Paper sx={{ p: 3, mb: 2 }}>
                                    <Typography variant="h6" gutterBottom>Financial Summary</Typography>
                                    <Grid container spacing={3}>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light', borderRadius: 1 }}>
                                                <Typography variant="body2" color="primary.contrastText">Budget</Typography>
                                                <Typography variant="h5" color="primary.contrastText" fontWeight="bold">
                                                    {formatCurrency(selectedProjectData.budget)}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                                                <Typography variant="body2" color="warning.contrastText">Actual Cost</Typography>
                                                <Typography variant="h5" color="warning.contrastText" fontWeight="bold">
                                                    {formatCurrency(selectedProjectData.actual_cost)}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                            <Box sx={{ textAlign: 'center', p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                                                <Typography variant="body2" color="info.contrastText">Revenue</Typography>
                                                <Typography variant="h5" color="info.contrastText" fontWeight="bold">
                                                    {formatCurrency(selectedProjectData.billable_amount)}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                                            <Box sx={{
                                                textAlign: 'center',
                                                p: 2,
                                                bgcolor: selectedProjectData.profit_loss > 0 ? 'success.light' : 'error.light',
                                                borderRadius: 1
                                            }}>
                                                <Typography variant="body2" color={selectedProjectData.profit_loss > 0 ? 'success.contrastText' : 'error.contrastText'}>
                                                    Profit/Loss
                                                </Typography>
                                                <Typography variant="h5" fontWeight="bold"
                                                    color={selectedProjectData.profit_loss > 0 ? 'success.contrastText' : 'error.contrastText'}>
                                                    {formatCurrency(selectedProjectData.profit_loss)}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>

                            {/* Key Metrics */}
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>Performance Metrics</Typography>
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" color="text.secondary">Profit Margin</Typography>
                                            <Typography variant="h4" color={getProfitColor(selectedProjectData.profit_margin)}>
                                                {formatNullablePercent(asDisplayValue(selectedProjectData.profit_margin))}
                                            </Typography>
                                        </Box>
                                        <Box sx={{ mb: 2 }}>
                                            <Typography variant="body2" color="text.secondary">Budget Variance</Typography>
                                            <Typography variant="h4" color={getProfitColor(-selectedProjectData.variance_percentage)}>
                                                {formatNullablePercent(asDisplayValue(selectedProjectData.variance_percentage))}
                                            </Typography>
                                        </Box>
                                        <Box>
                                            <Typography variant="body2" color="text.secondary">Health Score</Typography>
                                            <Typography variant="h4" color={getHealthColor(asDisplayValue(selectedProjectData.health_score)) + '.main'}>
                                                {formatNullablePercent(asDisplayValue(selectedProjectData.health_score) / 1)}
                                            </Typography>
                                        </Box>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* Project Insights */}
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" gutterBottom>Project Insights</Typography>
                                        {selectedProjectData.profit_margin > 20 && (
                                            <Alert severity="success" sx={{ mb: 2 }}>
                                                <strong>Excellent Performance:</strong> High profit margin indicates efficient project execution.
                                            </Alert>
                                        )}
                                        {selectedProjectData.variance_percentage > 20 && (
                                            <Alert severity="warning" sx={{ mb: 2 }}>
                                                <strong>Budget Concern:</strong> Project is significantly over budget. Review cost controls.
                                            </Alert>
                                        )}
                                        {selectedProjectData.profit_loss < 0 && (
                                            <Alert severity="error" sx={{ mb: 2 }}>
                                                <strong>Loss Alert:</strong> Project is operating at a loss. Immediate review recommended.
                                            </Alert>
                                        )}
                                        <Typography variant="body2" color="text.secondary">
                                            This analysis follows industry-standard PSA financial performance metrics
                                            used by leading platforms like Kantata, BigTime, and FinancialForce PSA.
                                        </Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseProjectDetail}>Close</Button>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={handleReallocateResources}
                        disabled={!selectedProjectData?.id}
                    >
                        Reallocate Resources
                    </Button>
                    <Button variant="contained" onClick={() => handleExport('PDF')}>
                        Export Report
                    </Button>
                </DialogActions>
            </Dialog>
        );
    };

    // Drill-down handler for KPIs and charts
    const handleKPIDrillDown = (metricKey) => {
        let config = { title: '', subtitle: '', data: [], columns: [] };

        switch (metricKey) {
            case 'wip':
                config = {
                    title: 'Work in Progress (WIP) Details',
                    subtitle: 'Approved, billable, uninvoiced time entries',
                    data: wipData?.entries || [],
                    columns: [
                        { key: 'project_name', label: 'Project' },
                        { key: 'user_name', label: 'Resource' },
                        { key: 'hours', label: 'Hours', align: 'right' },
                        { key: 'rate', label: 'Rate', align: 'right', format: (v) => formatCurrency(v) },
                        { key: 'value', label: 'Value', align: 'right', format: (v) => formatCurrency(v) },
                        { key: 'age_days', label: 'Age (Days)', align: 'right' }
                    ]
                };
                break;
            case 'ar':
                config = {
                    title: 'Accounts Receivable Details',
                    subtitle: 'Outstanding invoices by aging bucket',
                    data: arAgingData?.invoices || [],
                    columns: [
                        { key: 'client_name', label: 'Client' },
                        { key: 'invoice_number', label: 'Invoice #' },
                        { key: 'amount', label: 'Amount', align: 'right', format: (v) => formatCurrency(v) },
                        { key: 'days_outstanding', label: 'Days Old', align: 'right' },
                        { key: 'status', label: 'Status' }
                    ]
                };
                break;
            case 'revenue':
                config = {
                    title: 'Revenue Breakdown',
                    subtitle: 'Revenue by project',
                    data: costAnalytics?.budgetVariance?.projects || [],
                    columns: [
                        { key: 'name', label: 'Project' },
                        { key: 'total_revenue', label: 'Revenue', align: 'right', format: (v) => formatCurrency(v) },
                        { key: 'total_costs', label: 'Costs', align: 'right', format: (v) => formatCurrency(v) },
                        { key: 'profit_margin', label: 'Margin', align: 'right', format: (v) => formatNullablePercent(v) },
                        { key: 'status', label: 'Status' }
                    ]
                };
                break;
            default:
                return;
        }

        setDrillDownConfig(config);
        setDrillDownOpen(true);
    };

    // Summary bar and KPI cards: portfolio-health totals only (matches Executive / Overview scope).
    const { scopedRevenue, scopedNetProfit } = buildScopedFinancialMetrics({
        profitabilityData,
    });

    const executiveSummaryMetrics = {
        totalRevenue: scopedRevenue,
        revenueTrend: periodComparisonData?.variance?.revenue_recognized_pct || null,
        netProfit: scopedNetProfit,
        profitTrend: periodComparisonData?.variance?.cash_collected_pct || null,
        utilization: utilizationData?.current?.average_utilization ?? null,
        utilizationTarget: utilizationData?.current?.target_utilization || 80,
        dso: arAgingData?.dso ?? null,
        dsoPrior: null,
        healthScore: profitabilityData?.portfolio_health_score ?? null
    };

    const renderableProfitabilityData = profitabilityData || {
        total_revenue: null,
        net_profit: null,
        total_costs: null,
        profit_margin: null,
        roi: null,
        portfolio_health_score: null,
        total_projects: null,
        risk_level: 'unknown',
        risk_factors: []
    };
    const cardRevenue = scopedRevenue;
    const cardNetProfit = renderableProfitabilityData.net_profit;
    const isCardLoading = (endpointKey) => Boolean(
        loading
        || (endpointLoadingState && endpointLoadingState[endpointKey])
    );

    const hasCoreFinancialData = Boolean(
        profitabilityData
        || revenueTriadData
        || realizationData
        || wipData
        || backlogData
    );
    const showPortfolioKpiRow = hasCoreFinancialData;

    const showInitialContentSkeleton = !hasCoreFinancialData && !error;

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }} data-testid="enhanced-financial-dashboard">
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                    <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <AnalyticsIcon color="primary" fontSize="large" />
                        <span>Financial Management</span>
                        <span style={{ display: 'none' }}>Enhanced Financial Management</span>
                    </Typography>
                    <Typography variant="subtitle2" color="text.secondary">
                        PSA-standard financial analytics with drill-down capabilities
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <RefreshIcon />}
                        onClick={refreshFinancialData}
                        disabled={loading}
                        size="small"
                        aria-label="Refresh financial data"
                    >
                        Refresh All Data
                    </Button>
                </Box>
            </Box>

            <DashboardScopeBar
                periodLabel={getDashboardPeriodLabel(financialTimeframe)}
                dateRangeLabel={formatDashboardDateRangeLabel(financialDateRange)}
                scopeNote="Revenue, margin, and AR metrics are calculated by the financial engine."
                testId="dashboard-scope-bar"
            />

            {!showInitialContentSkeleton && (
                <>
                    {showPortfolioKpiRow && (
                        <ExecutiveSummaryBar
                            metrics={executiveSummaryMetrics}
                            loading={loading}
                            onMetricClick={handleKPIDrillDown}
                            maxMetrics={isProjectScoped ? 5 : 3}
                            testId={
                                isProjectScoped
                                    ? 'financial-executive-summary'
                                    : 'financial-portfolio-kpi-row'
                            }
                        />
                    )}

                    {!isProjectScoped && (
                        <Alert severity="info" sx={{ mb: 2 }} data-testid="financial-portfolio-scope-hint">
                            Portfolio summary and KPI grid below use financial-engine totals for the selected period.
                            {financialWorkQueueStats.total > 0 && (
                                <>
                                    {' '}
                                    {financialWorkQueueStats.total}
                                    {' '}
                                    work queue item(s) need action — expand the queue below.
                                </>
                            )}
                            {' '}
                            Select a project in the scope bar to narrow metrics, or open
                            {' '}
                            <RouterLink to="/project-operations">Project operations</RouterLink>
                            {' '}
                            for delivery control.
                        </Alert>
                    )}

                    {/* Enhanced Filters - Date range, client, project, comparison toggle */}
                    <FinancialFilters
                        filters={filters}
                        onFiltersChange={updateFilters}
                        clients={clients}
                        projects={projects}
                        serviceLines={[]}
                        loading={loading}
                        showComparison={showComparison}
                        onComparisonToggle={toggleComparison}
                        onExport={exportData}
                    />

                    <DashboardActionInbox
                        title="Financial work queue"
                        subtitle="Margin, staffing, billing, and data-quality items that need an owner and next action."
                        stats={financialWorkQueueStats}
                        testId="financial-work-queue-inbox"
                        defaultExpanded={!isProjectScoped}
                    >
                        <FinancialWorkItemsPanel
                            qualityEntries={qualitySummary}
                            projectId={scopedProjectId}
                            embedded
                            onStatsChange={setFinancialWorkQueueStats}
                        />
                    </DashboardActionInbox>

                    {/* Error Alert — fatal only when no core KPI payload loaded */}
                    {error && !loading && !hasCoreFinancialData && (
                        <Alert
                            severity="error"
                            onClose={clearError}
                            sx={{ mb: 2 }}
                        >
                            {error}
                        </Alert>
                    )}

                    {/* Drill-Down Dialog */}
                    <DrillDownDialog
                        open={drillDownOpen}
                        onClose={() => setDrillDownOpen(false)}
                        title={drillDownConfig.title}
                        subtitle={drillDownConfig.subtitle}
                        data={drillDownConfig.data}
                        columns={drillDownConfig.columns}
                        loading={loading}
                        onExport={(data) => {
                            // Generate CSV from drill-down data
                            const csv = [
                                drillDownConfig.columns.map(c => c.label).join(','),
                                ...data.map(row =>
                                    drillDownConfig.columns.map(c => row[c.key] ?? '').join(',')
                                )
                            ].join('\n');
                            const blob = new Blob([csv], { type: 'text/csv' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url;
                            a.download = `${drillDownConfig.title.replace(/\s+/g, '_')}.csv`;
                            a.click();
                        }}
                    />
                </>
            )}

            {showInitialContentSkeleton && (
                <DashboardLoadingState count={8} testId="financial-dashboard-loading" />
            )}

            {/* Main Tabs — visible during load so users see page structure */}
            <Card sx={{ mb: 3 }}>
                <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    variant="scrollable"
                    scrollButtons="auto"
                    sx={{ borderBottom: 1, borderColor: 'divider' }}
                >
                    <Tab icon={<DashboardIcon />} label="Portfolio Overview" />
                    <Tab icon={<TrendingUpIcon />} label="Revenue Forecasting" />
                    <Tab icon={<MoneyIcon />} label="Cost Analytics" />
                    <Tab icon={<AssessmentIcon />} label="Budget Variance" />
                    <Tab icon={<TableChartIcon />} label="Project Financial Details" />
                    <Tab icon={<ExportIcon />} label="Export & Reports" />
                </Tabs>

                {/* Portfolio Overview Tab */}
                <TabPanel value={activeTab} index={0}>
                    {hasCoreFinancialData ? (
                        <FinancialEngagementKpiGrid
                            isProjectScoped={isProjectScoped}
                            cardRevenue={cardRevenue}
                            cardNetProfit={cardNetProfit}
                            renderableProfitabilityData={renderableProfitabilityData}
                            periodComparisonData={periodComparisonData}
                            showComparison={showComparison}
                            isCardLoading={isCardLoading}
                            onKpiDrillDown={handleKPIDrillDown}
                            asDisplayValue={asDisplayValue}
                            formatCurrency={formatCurrency}
                            formatPercentage={formatPercentage}
                            formatNullableRate={formatNullableRate}
                            utilizationData={utilizationData}
                            billRateData={billRateData}
                            realizationData={realizationData}
                            wipData={wipData}
                            backlogData={backlogData}
                            contributionMarginData={contributionMarginData}
                            arAgingData={arAgingData}
                            revenueTriadData={revenueTriadData}
                            cashFlowData={cashFlowData}
                            expenseRatiosData={expenseRatiosData}
                            benchmarkCompareData={benchmarkCompareData}
                        />
                    ) : (
                        <DashboardLoadingState count={8} testId="financial-portfolio-overview-loading" />
                    )}
                </TabPanel>

                {/* Revenue Forecasting Tab */}
                <TabPanel value={activeTab} index={1}>
                    {revenueForecasting ? (
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, md: 8 }}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 2 }}>Revenue Forecast</Typography>
                                        <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                                            Enhanced Revenue Forecast (90 Days)
                                        </Typography>

                                        {/* Enhanced Forecast Components */}
                                        {revenueForecasting.components && (
                                            <Grid container spacing={2} sx={{ mb: 3 }}>
                                                <Grid size={{ xs: 12, md: 3 }}>
                                                    <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                                                        <Typography variant="h6" color="primary">
                                                            {formatCurrency(revenueForecasting.components.actual_to_date)}
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary">
                                                            Actual to Date
                                                        </Typography>
                                                    </Card>
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 3 }}>
                                                    <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                                                        <Typography variant="h6" color="success.main">
                                                            {formatCurrency(revenueForecasting.components.scheduled_billings)}
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary">
                                                            Scheduled Billings
                                                        </Typography>
                                                    </Card>
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 3 }}>
                                                    <Card variant="outlined" sx={{ textAlign: 'center', p: 2 }}>
                                                        <Typography variant="h6" color="warning.main">
                                                            {formatCurrency(revenueForecasting.components.wip_velocity)}
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary">
                                                            WIP Velocity
                                                        </Typography>
                                                    </Card>
                                                </Grid>
                                                <Grid size={{ xs: 12, md: 3 }}>
                                                    <Card variant="outlined" sx={{ textAlign: 'center', p: 2, bgcolor: 'primary.light' }}>
                                                        <Typography variant="h6" color="white">
                                                            {formatCurrency(revenueForecasting.components.total_forecast)}
                                                        </Typography>
                                                        <Typography variant="body2" color="white">
                                                            Total Forecast
                                                        </Typography>
                                                    </Card>
                                                </Grid>
                                            </Grid>
                                        )}

                                        {/* Forecast Chart */}
                                        <ResponsiveContainer width="100%" height={300}>
                                            <LineChart data={revenueForecasting.forecast || []}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="month" />
                                                <YAxis />
                                                <RechartsTooltip />
                                                <Legend />
                                                <Line type="monotone" dataKey="predicted" stroke="#2196f3" strokeWidth={2} />
                                                <Line type="monotone" dataKey="upper_bound" stroke="#4caf50" strokeDasharray="5 5" />
                                                <Line type="monotone" dataKey="lower_bound" stroke="#f44336" strokeDasharray="5 5" />
                                            </LineChart>
                                        </ResponsiveContainer>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid size={{ xs: 12, md: 4 }}>
                                <Grid container spacing={2}>
                                    <Grid size={12}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h6">Forecast Confidence</Typography>
                                                <Typography variant="h3" color="primary">
                                                    {formatPercentage(revenueForecasting.confidence)}
                                                </Typography>
                                                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                                                    {revenueForecasting.confidence_level || 'High'} Confidence
                                                </Typography>
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                    <Grid size={12}>
                                        <Card>
                                            <CardContent>
                                                <Typography variant="h6">Forecast Method</Typography>
                                                <Typography variant="body2" sx={{ mb: 1 }}>
                                                    {revenueForecasting.methodology || 'Scheduled + WIP velocity'}
                                                </Typography>
                                                {revenueForecasting.assumptions && (
                                                    <Box sx={{ mt: 2 }}>
                                                        <Typography variant="subtitle2" color="textSecondary">
                                                            Key Assumptions:
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary">
                                                            • WIP Conversion: {revenueForecasting.assumptions.wip_conversion_rate * 100}%
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary">
                                                            • Schedule Adherence: {revenueForecasting.assumptions.schedule_adherence}
                                                        </Typography>
                                                        <Typography variant="body2" color="textSecondary">
                                                            • Market Conditions: {revenueForecasting.assumptions.market_conditions}
                                                        </Typography>
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Card>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>
                    ) : (
                        <Box display="flex" justifyContent="center" p={4}>
                            <CircularProgress />
                        </Box>
                    )}
                </TabPanel>

                {/* Cost Analytics Tab */}
                <TabPanel value={activeTab} index={2}>
                    {costAnalytics && (
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 2 }}>Project Profitability</Typography>
                                        <ResponsiveContainer width="100%" height={300}>
                                            <BarChart data={costAnalytics.profitabilityBreakdown.by_project.slice(0, 5)}>
                                                <CartesianGrid strokeDasharray="3 3" />
                                                <XAxis dataKey="name" />
                                                <YAxis />
                                                <RechartsTooltip />
                                                <Legend />
                                                <Bar dataKey="profit" fill="#2196f3" />
                                            </BarChart>
                                        </ResponsiveContainer>
                                    </CardContent>
                                </Card>
                            </Grid>
                            <Grid size={{ xs: 12, md: 6 }}>
                                <Card>
                                    <CardContent>
                                        <Typography variant="h6" sx={{ mb: 2 }}>Department Performance</Typography>
                                        <ResponsiveContainer width="100%" height={300}>
                                            <PieChart>
                                                <Pie
                                                    data={costAnalytics.profitabilityBreakdown.by_department}
                                                    cx="50%"
                                                    cy="50%"
                                                    labelLine={false}
                                                    label={({ name, value }) => `${name}: ${formatCurrency(value)}`}
                                                    outerRadius={80}
                                                    fill="#8884d8"
                                                    dataKey="profit"
                                                >
                                                    {costAnalytics.profitabilityBreakdown.by_department.map((entry, index) => (
                                                        <Cell key={`cell-${index}`} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                                                    ))}
                                                </Pie>
                                                <RechartsTooltip />
                                            </PieChart>
                                        </ResponsiveContainer>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    )}
                </TabPanel>

                {/* Budget Variance Tab */}
                <TabPanel value={activeTab} index={3}>
                    {costAnalytics && costAnalytics.budgetVariance.projects.length > 0 ? (
                        <TableContainer component={Paper}>
                            <Table>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Project Name</TableCell>
                                        <TableCell align="right">Budget</TableCell>
                                        <TableCell align="right">Actual Cost</TableCell>
                                        <TableCell align="right">Variance</TableCell>
                                        <TableCell align="right">Variance %</TableCell>
                                        <TableCell>Status</TableCell>
                                        <TableCell align="right">Action</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {costAnalytics.budgetVariance.projects.map((project) => (
                                        <TableRow key={project.id}>
                                            <TableCell component="th" scope="row">
                                                {project.name}
                                            </TableCell>
                                            <TableCell align="right">{formatCurrency(project.budget)}</TableCell>
                                            <TableCell align="right">{formatCurrency(project.actual_cost)}</TableCell>
                                            <TableCell align="right">
                                                <Chip
                                                    label={formatCurrency(project.variance)}
                                                    color={project.variance > 0 ? 'error' : 'success'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                <Chip
                                                    label={formatNullablePercent(project.variance_percentage)}
                                                    color={project.variance_percentage > 0 ? 'error' : 'success'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={project.status}
                                                    color={project.status === 'active' ? 'primary' : 'default'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell align="right">
                                                {project.variance_percentage > 0 && (
                                                    <Button
                                                        component={RouterLink}
                                                        to={buildProjectOpsWedgeUrl(project.id, 'control')}
                                                        size="small"
                                                        variant="outlined"
                                                    >
                                                        Control center
                                                    </Button>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                            <Typography variant="h6" color="text.secondary" gutterBottom>
                                Budget Variance Data Unavailable
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {error ? `Error: ${error}` : 'Unable to load budget variance data. Please refresh the page or check your connection.'}
                            </Typography>
                            {costAnalytics?.exportCapabilities?.status === 'error' && (
                                <Typography variant="body2" color="error.main" sx={{ mt: 1 }}>
                                    Data source connection failed. Please try again later.
                                </Typography>
                            )}
                        </Box>
                    )}
                </TabPanel>

                {/* Project Financial Details Tab */}
                <TabPanel value={activeTab} index={4}>
                    <ProjectFinancialDetailsTable />
                </TabPanel>

                {/* Export & Reports Tab */}
                <TabPanel value={activeTab} index={5}>
                    <Grid container spacing={3}>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" sx={{ mb: 2 }}>Export Options</Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            startIcon={<CsvIcon />}
                                            onClick={() => handleExport('CSV')}
                                            fullWidth
                                        >
                                            Export to CSV
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<PdfIcon />}
                                            onClick={() => handleExport('PDF')}
                                            fullWidth
                                        >
                                            Export to PDF
                                        </Button>
                                        <Button
                                            variant="outlined"
                                            startIcon={<ExcelIcon />}
                                            onClick={() => handleExport('Excel')}
                                            fullWidth
                                        >
                                            Export to Excel
                                        </Button>
                                    </Box>
                                </CardContent>
                            </Card>
                        </Grid>
                        <Grid size={{ xs: 12, md: 6 }}>
                            <Card>
                                <CardContent>
                                    <Typography variant="h6" sx={{ mb: 2 }}>Export Status</Typography>
                                    <Typography color="text.secondary">
                                        Export capabilities: Ready
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 1 }}>
                                        Available format: CSV
                                    </Typography>
                                    <Alert severity="info" sx={{ mt: 2 }}>
                                        CSV export includes portfolio summary, revenue triad, and project performance data.
                                        PDF and Excel exports require backend report generation service.
                                    </Alert>
                                </CardContent>
                            </Card>
                        </Grid>
                    </Grid>
                </TabPanel>
            </Card>

            {/* Project Detail Dialog */}
            <ProjectDetailDialog />
        </Container>
    );
};

export default EnhancedFinancialDashboard; 